import { useMemo, useState } from "react";

import { CustomRiskLevel } from "src/core/constants/risk";
import { useFolder, useFolderCurrentVersion } from "src/core/contexts/FolderContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { Mission } from "src/core/gql/graphql";
import { mapMissionToIMission } from "src/core/mappers/folder.mapper";
import { mapCodeToCustomStatus } from "src/core/utils/status";

export type IMission = {
  id: string;
  missionType: string;
  missionTypeCode: string;
  renewDate: string | null;
  isClosable: boolean;
  isEditable: boolean;
  risk: CustomRiskLevel;
  isActive: boolean;
  status: string;
  statusComment?: string;
  lastModification: string | null;
  creation: string | null;
};

const isMissionCreatedAfterFolderFile = (
  mission: IMission,
  folderFileCreation: string | null
): boolean =>
  Boolean(
    mission.creation &&
      folderFileCreation &&
      new Date(mission.creation) > new Date(folderFileCreation)
  );

const getMissionSortPriority = (mission: IMission, folderFileCreation: string | null): number => {
  if (!isMissionCreatedAfterFolderFile(mission, folderFileCreation) && mission.isClosable) {
    return 0;
  }
  if (isMissionCreatedAfterFolderFile(mission, folderFileCreation)) {
    return 1;
  }
  return 2;
};

const sortActiveMissions = (missions: IMission[], folderFileCreation: string | null) =>
  [...missions].sort((a, b) => {
    const priorityDiff =
      getMissionSortPriority(a, folderFileCreation) - getMissionSortPriority(b, folderFileCreation);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return a.missionType.localeCompare(b.missionType, "fr", { sensitivity: "base" });
  });

export const useFolderMissions = () => {
  const { folderFile, isReadOnly } = useFolder();
  const { missionTypeWithRiskOptions } = useRiskOptions();
  const currentVersion = useFolderCurrentVersion();

  const isCurrentVersion = currentVersion?.id === folderFile?.folderFile?.id;
  const folderState = useMemo(
    () => mapCodeToCustomStatus(folderFile.folderFile?.workflowStatus?.state?.code),
    [folderFile.folderFile?.workflowStatus?.state?.code]
  );

  const allMissions = useMemo<IMission[]>(() => {
    if (!folderFile) return [];

    const missions = isCurrentVersion
      ? folderFile?.folderFile?.currentCustomerFolder?.missions
      : folderFile?.folderFile?.fileCustomerFolder?.missions;

    if (!missions || missions.length === 0) return [];

    return missions.map((mission) =>
      mapMissionToIMission(mission as Mission, missionTypeWithRiskOptions)
    );
  }, [folderFile, isCurrentVersion, missionTypeWithRiskOptions]);

  const folderFileCreation = folderFile?.folderFile?.adminStatus?.creation ?? null;

  const activeMissions = useMemo(
    () =>
      sortActiveMissions(
        allMissions.filter((mission) => mission.isActive),
        folderFileCreation
      ),
    [allMissions, folderFileCreation]
  );

  const inactiveMissions = useMemo(
    () =>
      [...allMissions.filter((mission) => !mission.isActive)].sort((a, b) =>
        a.missionType.localeCompare(b.missionType, "fr", { sensitivity: "base" })
      ),
    [allMissions]
  );

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openRenewModal, setOpenRenewModal] = useState(false);
  const [openUpsertModal, setOpenUpsertModal] = useState(false);
  const [openCloseModal, setOpenCloseModal] = useState(false);
  const [missionToEdit, setMissionToEdit] = useState<IMission | null>(null);

  const renewMission = (mission: IMission) => {
    setMissionToEdit(mission);
    setOpenRenewModal(true);
  };
  const deleteMission = (mission: IMission) => {
    setMissionToEdit(mission);
    setOpenDeleteModal(true);
  };
  const editMission = (mission: IMission) => {
    setMissionToEdit(mission);
    setOpenUpsertModal(true);
  };
  const closeMission = (mission: IMission) => {
    setMissionToEdit(mission);
    setOpenCloseModal(true);
  };

  const states = {
    openDeleteModal,
    setOpenDeleteModal,
    openUpsertModal,
    setOpenUpsertModal,
    openRenewModal,
    setOpenRenewModal,
    missionToEdit,
    setMissionToEdit,
    openCloseModal,
    setOpenCloseModal,
  };

  return {
    activeMissions,
    inactiveMissions,
    renewMission,
    deleteMission,
    editMission,
    closeMission,
    states,
    isReadOnly,
    folderFileCreation,
    folderState,
  };
};
